import { motion } from 'framer-motion'
import { GitBranch, Target, Zap, CheckCircle } from 'lucide-react'

const platforms = [
    {
        id: 'nexus',
        icon: GitBranch,
        name: 'Nexus Flow Engine™',
        tagline: 'Agent Orchestration Redefined',
        description:
            'Build, test, and deploy sophisticated multi-agent workflows with our intuitive visual designer. Leverage the power of LangGraph and AutoGen frameworks without the complexity.',
        features: [
            'Drag-and-drop workflow builder',
            'Real-time collaboration',
            'Version control integration',
            'Automated testing suite',
            'Performance monitoring',
            'Cost optimization tools',
        ],
        capabilities: [
            'Multi-step reasoning chains',
            'Tool and API integration',
            'Human-in-the-loop workflows',
            'Error handling and retries',
        ],
        gradient: 'from-primary-500 to-blue-500',
    },
    {
        id: 'vertex',
        icon: Target,
        name: 'Vertex Retrieval Unit',
        tagline: 'High-Performance RAG at Scale',
        description:
            'Transform your enterprise knowledge base into a powerful AI assistant. Our optimized RAG system delivers accurate, contextual responses with industry-leading speed.',
        features: [
            'Vector database optimization',
            'Semantic search engine',
            'Context window management',
            'Source attribution',
            'Multi-modal retrieval',
            'Knowledge graph integration',
        ],
        capabilities: [
            'Sub-100ms retrieval latency',
            'Billion-scale document indexing',
            'Advanced prompt engineering (RIO)',
            'Automatic chunk optimization',
        ],
        gradient: 'from-secondary-500 to-purple-500',
    },
    {
        id: 'apex',
        icon: Zap,
        name: 'Apex Speed Core',
        tagline: 'Blazing-Fast Inference',
        description:
            'Maximize your LLM performance with our battle-tested inference engine. Deep CUDA/TensorRT optimizations deliver the lowest latency and highest throughput in the industry.',
        features: [
            'Model quantization (INT8/FP16)',
            'Layer fusion optimization',
            'Dynamic batching',
            'Multi-GPU orchestration',
            'Custom kernel implementations',
            'Real-time performance metrics',
        ],
        capabilities: [
            '10x faster than baseline',
            '50% cost reduction',
            'Zero-downtime updates',
            'Auto-scaling infrastructure',
        ],
        gradient: 'from-accent-500 to-pink-500',
    },
]

export const PlatformPage = () => {
    return (
        <main className="pt-20">
            {/* Hero Section */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 -z-10" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent -z-10" />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
                            The <span className="gradient-text">AgentForge</span> Platform
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-400 max-w-4xl mx-auto">
                            A comprehensive LLMOps platform designed to accelerate your journey
                            from AI experimentation to production-grade deployment
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Platform Features */}
            {platforms.map((platform, index) => {
                const Icon = platform.icon
                const isEven = index % 2 === 0

                return (
                    <section
                        key={platform.id}
                        id={platform.id}
                        className={`py-24 ${isEven ? 'bg-slate-900/50' : 'bg-slate-950'}`}
                    >
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="grid lg:grid-cols-2 gap-12 items-center">
                                {/* Content */}
                                <motion.div
                                    initial={{ opacity: 0, x: isEven ? -20 : 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6 }}
                                    className={isEven ? 'lg:order-1' : 'lg:order-2'}
                                >
                                    <div
                                        className={`w-16 h-16 rounded-xl bg-gradient-to-br ${platform.gradient} flex items-center justify-center mb-6 shadow-lg`}
                                    >
                                        <Icon className="w-8 h-8 text-white" />
                                    </div>

                                    <h2 className="text-4xl font-bold mb-4">{platform.name}</h2>
                                    <p className={`text-lg font-semibold mb-4 bg-gradient-to-r ${platform.gradient} bg-clip-text text-transparent`}>
                                        {platform.tagline}
                                    </p>
                                    <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                                        {platform.description}
                                    </p>

                                    <h3 className="text-xl font-semibold mb-4">Key Capabilities</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                                        {platform.capabilities.map((capability, cIndex) => (
                                            <div
                                                key={cIndex}
                                                className="flex items-start space-x-2 text-slate-300"
                                            >
                                                <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                                                <span>{capability}</span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>

                                {/* Features Card */}
                                <motion.div
                                    initial={{ opacity: 0, x: isEven ? 20 : -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6 }}
                                    className={isEven ? 'lg:order-2' : 'lg:order-1'}
                                >
                                    <div className="p-8 rounded-2xl bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm">
                                        <h3 className="text-2xl font-bold mb-6">Platform Features</h3>
                                        <ul className="space-y-4">
                                            {platform.features.map((feature, fIndex) => (
                                                <li
                                                    key={fIndex}
                                                    className="flex items-start space-x-3 text-slate-300"
                                                >
                                                    <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${platform.gradient} mt-2 flex-shrink-0`} />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </section>
                )
            })}

            {/* CTA Section */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-900/20 via-secondary-900/20 to-accent-900/20" />

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Ready to Experience the Power?
                        </h2>
                        <p className="text-xl text-slate-400 mb-8">
                            See how AgentForge can transform your AI operations
                        </p>
                        <button className="px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40">
                            Schedule a Demo
                        </button>
                    </motion.div>
                </div>
            </section>
        </main>
    )
}

